import { type UIEvent, useState } from 'react';

import { ToastManager } from '../../ToastManager.jsx';
import { CloseButton } from '../buttons/CloseButton';
import { SpinnerLoading } from '../common/SpinnerLoading';
import { Dialog } from '../overlay/Dialog';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useNotificationsById } from '../../store/notifications/useNotifications';
import { useApi } from '../../store/SelectedHomeyStore.hooks';
import { useUserMe } from '../../store/users/useUserMe';

import { theme } from '../../theme/theme';
import styles from './NotificationsDialog.module.css';

import { Notifications } from './Notifications';

interface NotificationsDialogProps {
  onClose: () => void;
}

/**
 * Centered dialog that lists the Timeline notifications of the selected Homey.
 */
export function NotificationsDialog(props: NotificationsDialogProps) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const { userMe } = useUserMe();
  const notifications = useNotificationsById();
  const [isScrolled, setIsScrolled] = useState(false);

  const title = i18n.messageFormatter('notifications.title');

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    setIsScrolled(event.currentTarget.scrollTop > 0);
  }

  function handleRequestDelete(params: { id: string }) {
    api?.notifications.deleteNotification({ id: params.id }).catch(ToastManager.handleError);
  }

  return (
    <Dialog aria-label={title} onClose={props.onClose}>
      <div className={styles.root}>
        <div className={styles.header} data-is-scrolled={isScrolled}>
          <h2 className={styles.title}>{title}</h2>

          <CloseButton className={styles.closeButton} onPress={props.onClose} />
        </div>

        <div className={styles.body} onScroll={handleScroll}>
          {notifications.isLoading === true && (
            <div className={styles.loading}>
              <SpinnerLoading size={32} color={theme.color.mono_o_20} rainbow={false} />
            </div>
          )}

          {notifications.isLoading !== true && (
            <Notifications
              userMe={userMe}
              notificationsById={notifications.byId}
              onRequestDelete={handleRequestDelete}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}
